import React from 'react';
import {
  Box, Paper, Typography, Button, Grid, IconButton, Popover,
} from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';

export function IncidentDrawerDetailStep3({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as any;
  return (
    <Box ref={s.drawerStep3Ref} sx={s.stepSectionSx('step3')}>
      <Paper variant="outlined" sx={s.drawerCardSx}>
      {!s.groupedReviewMode ? (
        <Typography sx={s.cardLabelSx}>{s.journeyPhaseTitleForStep('step3')} phase</Typography>
      ) : null}
      {s.renderAuditStepHeading(
        'step3',
        'Step 3: Verify trigger',
        s.groupedReviewMode
          ? undefined
          : 'Confirm which detection rule fired, what the rule matched, and whether this is a recurring safety issue or a system-side problem.',
      )}
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Alert type</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.currentTitle}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
            <Typography variant="caption" color="text.secondary">Detection rule</Typography>
            <IconButton
              size="small"
              aria-label="Why did this trigger?"
              onClick={(event) => s.setRuleHelpAnchorEl(event.currentTarget)}
              sx={{ p: 0.25 }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.detectionRuleSummary || '—'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">What the rule saw</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {s.detectionFindingSummary || '—'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Affected entity</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.affectedEntitySummary || 'Entity not identified'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Confidence</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.confidenceSummary}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Pattern observations in window</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.relatedEventSignalsSummary}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Recurring issue</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.recurringIssueSummary}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Detection/system issue</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.detectionSystemIssueSummary}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Area behavior</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.areaBehaviorSummary || '—'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Sensitivity</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.sensitivitySummary || '—'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">What to confirm</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.reviewFocusSummary}</Typography>
        </Grid>
        {s.isPpeIncident && (() => {
          const miss = s._ppeMissingItemsFromIncident(s.selectedIncident);
          const st = s._ppeStabilizedStateFromIncident(s.selectedIncident);
          if (miss.length === 0 && !st) return null;
          return (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">PPE detail</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {miss.length > 0 ? miss.map(s._titleCase).join(', ') : s._ppeItemsSummary(st)}
              </Typography>
            </Grid>
          );
        })()}
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, fontWeight: 700 }}>
        Need more detail?
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="text" onClick={s.openRiskAnalytics}>
          Learn more in analytics
        </Button>
        {s.relatedIncidentCount > 1 && (
          <Button size="small" variant="text" onClick={s.openRelatedIncidents}>
            View pattern observations
          </Button>
        )}
      </Box>
      <Popover
        open={Boolean(s.ruleHelpAnchorEl)}
        anchorEl={s.ruleHelpAnchorEl}
        onClose={() => s.setRuleHelpAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 360, maxWidth: 'min(92vw, 360px)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.8 }}>
            Why did this trigger?
          </Typography>
          <Typography variant="caption" color="text.secondary">Detection rule</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>{s.detectionRuleSummary || 'Detection rule was not reported.'}</Typography>
          <Typography variant="caption" color="text.secondary">What the rule matched</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{s.detectionFindingSummary || 'The observation details were not reported.'}</Typography>
          <Typography variant="caption" color="text.secondary">Area behavior / sensitivity</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {[s.areaBehaviorSummary || 'Area behavior not reported', s.sensitivitySummary || 'Sensitivity not reported'].join(' • ')}
          </Typography>
          <Typography variant="caption" color="text.secondary">What to confirm</Typography>
          <Typography variant="body2">{s.reviewFocusSummary}</Typography>
        </Box>
      </Popover>
      </Paper>
      {!s.groupedReviewMode ? s.renderStepNavigation('step3') : null}
    </Box>
  );
}
